import { Fragment, useEffect, useMemo, useState } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { GetProfilePhoto } from "../api/microsoft.api"
import Avatar from "./Shared/Avatar";
import { getAvatarName } from "../Helpers";
import useGraphApiToken from "../hooks/useGraphApiToken";
import useUserData from "../hooks/useUserData";
import entntlogo from "../entntlogo.svg";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function NavBar({
  setThemeIsDark,
  themeIsDark,
}: {
  setThemeIsDark: (state: boolean) => void;
  themeIsDark: boolean;
}) {
  const [current, setCurrent] = useState("Roles");
  const location = useLocation();
  const { instance } = useMsal();
  const graphToken = useGraphApiToken();
  const [profile, setProfile] = useState<string>();
  const { username, userId } = useUserData();

  useEffect(() => {
    if (!graphToken) return;
    
    GetProfilePhoto(graphToken)
      .then((photo) => {
        if (photo) {
          try {
            setProfile(URL.createObjectURL(photo));
          } catch (error) {
            console.error("Error creating object URL for profile photo:", error);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching profile photo:", error);
      });
  }, [graphToken]);

  useEffect(() => {
    let p = location.pathname.split("/");
    let option = p.length > 1 ? p[1] : "Roles";
    setCurrent(option);
  }, [location]);

  const navigation = useMemo(
    () => [
      {
        name: "Dashboard",
        href: "/dashboard",
        current: current === "Dashboard",
      },
      {
        name:
          current === "Workflows"
            ? "Workflows"
            : current === "Job Descriptions"
            ? "Job Descriptions"
            : "Roles",
        current:
          current === "Roles" ||
          current === "Workflows" ||
          current === "Job Descriptions",
        dropdownItems: [
          { name: "Roles", href: "/" },
          { name: "Workflows", href: "/workflows" },
          { name: "Job Descriptions", href: "/job-descriptions" },
        ],
      },
      {
        name: current === "Questions" 
        ? "Questions" 
        : current === "Sets" 
        ? "Sets" 
        : current === "Tests" ? "Tests" 
        : "Assessments",
        current: current === "Assessments" || current === "Sets" || current === "Questions" || current === "Tests",
        dropdownItems: [
          { name: "Assessments", href: "/assessments" },
          { name: "Sets", href: "/sets" },
          { name: "Questions", href: "/questions" },
          { name: "Tests", href: "/tests" },
        ],
      },
      {
        name: current === "Quarantine" ? "Quarantine" : "Rank Board",
        href: "/quarantine/companies",
        current: ["Quarantine", "quarantine", "Rank Board", "rankboard"].includes(current),
        dropdownItems: [
          { name: "Quarantine", href: "/quarantine" },
          { name: "Rank Board", href: "/rankboard" },
        ],
      },
      {
        name: "Onboarding",
        href: "/onboarding",
        current: current === "onboarding" || current === "Onboarding"
      },
      {
        name: "Issues",
        href: "/issues",
        current: ["Issues", "issues"].includes(current),
      }
      // {
      //   name: "Location Stats",
      //   href: "/location",
      //   current: ["Location Stats", "location"].includes(current),
      // },
    ],
    [current]
  );
  

  const NavDropdown = ({ item }: { item: typeof navigation[0] }) => {
    const [isOpen, setIsOpen] = useState(false);
  
    if (!item.dropdownItems) {
      return (
        <Link
          to={item.href!}
          className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium
            ${item.current
              ? "border-entntblue text-gray-900 dark:text-entntblue"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-white dark:text-gray-400 dark:hover:border-white"
            }`}
        >
          {item.name}
        </Link>
      );
    }
  
    return (
      <div
        className="relative inline-flex"  
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <button
          className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium
            ${item.current
              ? "border-entntblue text-gray-900 dark:text-entntblue"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-white dark:text-gray-400 dark:hover:border-white"
            }`}
        >
          {item.name}
          <ChevronDownIcon className="ml-1 h-4 w-4" />
        </button>
  
        <Transition
          show={isOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <div className="absolute left-0 top-5 z-10 mt-8 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight">
            {item.dropdownItems.map((dropdownItem) => (
              <Link
                key={dropdownItem.name}
                to={dropdownItem.href}
                className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg dark:hover:text-white"
                onClick={() => setCurrent(dropdownItem.name)}
              >
                {dropdownItem.name}
              </Link>
            ))}
          </div>
        </Transition>
      </div>
    );
  };

  useEffect(() => {
    if (location.pathname === "/") {
      setCurrent("Roles");
      return;
    }
    
    const currentPath = location.pathname.slice(1).split('/')[0];
    
    navigation.some((item) => {
      if (item.dropdownItems) {
        return item.dropdownItems.some(dropdownItem => {
          const dropdownPath = dropdownItem.href.slice(1).split('/')[0];
          if (currentPath === dropdownPath) {
            setCurrent(dropdownItem.name);
            return true;
          }
          return false;
        });
      } else if (item.href) {
        const itemPath = item.href.slice(1).split('/')[0];
        if (currentPath === itemPath) {
          setCurrent(item.name);
          return true;
        }
      }
      return false;
    });
  }, [location.pathname, navigation]);

  const handleSignout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  return (
    <Disclosure
      as="nav"
      className="bg-white shadow dark:bg-darkbg sticky top-0 z-40 dark:border-b dark:border-gray-500"
    >
      {({ open }) => (
        <>
          <div className="mx-auto px-2 sm:px-6 lg:px-8">
            <div className="relative flex h-16 justify-between">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-entntblue">
                  <span className="absolute -inset-0.5" />
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex flex-shrink-0 items-center">
                  <a href="https://www.entnt.in" className="-m-1.5 p-1.5">
                    <img
                      className="h-10 w-auto"
                      src={entntlogo}
                      alt="ENTNT"
                    />
                  </a>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                  {navigation.map((item) => (
                    <NavDropdown key={item.name} item={item} />
                  ))}
                </div>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                <span className="md:block hidden text-sm text-gray-500 mr-2 dark:text-gray-400">
                  {userId}
                </span>
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="relative flex rounded-full bg-white text-sm focus:outline-none ring-2 ring-entntblue ring-offset-2">
                      <span className="absolute -inset-1.5" />
                      <Avatar
                        src={profile}
                        avatarName={username && getAvatarName(username)}
                        height="h-8"
                        width="w-8"
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight">
                      <Menu.Item>
                        <span className="md:hidden flex px-4 py-2 text-sm text-gray-700 w-full bg-gray-200 dark:bg-darkbg dark:text-gray-400">
                          {userId}
                        </span>
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "flex px-4 py-2 text-sm text-gray-700 w-full text-left dark:text-gray-300 dark:hover:text-black"
                            )}
                            onClick={() => {
                              setThemeIsDark(!themeIsDark);
                            }}
                          >
                            <span>Mode :</span>
                            <span className="font-bold ml-2">
                              {themeIsDark ? "Dark" : "Light"}
                            </span>
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "block px-4 py-2 text-sm text-gray-700 w-full text-left dark:text-gray-300 dark:hover:text-black"
                            )}
                            onClick={() => {
                              handleSignout();
                            }}
                          >
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden">
            <div className="space-y-1 pb-4 pt-2">
              {navigation.map((item) => (
                <Fragment key={item.name}>
                  {item.dropdownItems ? (
                    <>
                      <Disclosure.Button
                        className={classNames(
                          "block w-full border-l-4 py-2 pl-3 pr-4 text-left text-base font-medium",
                          item.current
                            ? "border-entntblue bg-indigo-50 text-entntblue dark:bg-darkbglight"
                            : "border-transparent dark:text-gray-400 text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                        )}
                      >
                        {item.name}
                      </Disclosure.Button>
                      <div className="pl-4">
                        {item.dropdownItems.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="block border-l-4 border-transparent py-2 pl-3 pr-4 text-sm text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400"
                            onClick={() => setCurrent(dropdownItem.name)}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.href!}
                      onClick={() => setCurrent(item.name)}
                      className={classNames(
                        "block border-l-4 py-2 pl-3 pr-4 text-base font-medium",
                        item.current
                          ? "border-entntblue bg-indigo-50 text-entntblue dark:bg-darkbglight"
                          : "border-transparent dark:text-gray-400 text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </Fragment>
              ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}